import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { DropdownMenu, type DropdownMenuItem } from "./DropdownMenu";

const dropdownVariants = cva(
  "bg-surface-fill-primary-normal border border-surface-outline-neutral-subtle rounded-large",
  {
    variants: {
      size: {
        small: "w-64",
        medium: "w-80",
        large: "w-96",
      },
    },
    defaultVariants: {
      size: "medium",
    },
  }
);

export type DropdownItem = DropdownMenuItem;

export interface DropdownProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * Trigger element that opens the dropdown
   */
  trigger?: React.ReactNode;
  /**
   * Items to display in the dropdown
   */
  items?: DropdownItem[];
  /**
   * Custom content to render instead of the default item list
   */
  customContent?: React.ReactNode;
  /**
   * Section heading text
   */
  sectionHeading?: string;
  /**
   * Whether the dropdown is in loading state
   */
  isLoading?: boolean;
  /**
   * Whether to show empty/no results state
   */
  isEmpty?: boolean;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Empty state link text
   */
  emptyLinkText?: string;
  /**
   * Empty state link click handler
   */
  onEmptyLinkClick?: () => void;
  /**
   * Primary button text
   */
  primaryButtonText?: string;
  /**
   * Secondary button text
   */
  secondaryButtonText?: string;
  /**
   * Primary button click handler
   */
  onPrimaryClick?: () => void;
  /**
   * Secondary button click handler
   */
  onSecondaryClick?: () => void;
  /**
   * Dropdown size
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether dropdown is open (controlled)
   */
  open?: boolean;
  /**
   * Default open state (uncontrolled)
   */
  defaultOpen?: boolean;
  /**
   * Callback when open state changes
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Custom class for dropdown container
   */
  containerClassName?: string;
  /**
   * Custom class for dropdown menu
   */
  menuClassName?: string;
  /**
   * Show chevron on list items
   */
  showChevron?: boolean;
  /**
   * Custom empty state icon
   */
  emptyIcon?: React.ReactNode;
  /**
   * Disable footer buttons
   */
  disableFooter?: boolean;
  /**
   * Whether to show footer actions (overrides disableFooter)
   */
  showFooter?: boolean;
}

export const Dropdown = React.forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      className,
      trigger,
      items = [],
      customContent,
      sectionHeading,
      isLoading = false,
      isEmpty = false,
      emptyTitle = "No Search Results Found",
      emptyDescription = "Add description of what the user can search for here.",
      emptyLinkText = "Link to support site",
      onEmptyLinkClick,
      primaryButtonText = "Primary",
      secondaryButtonText = "Secondary",
      onPrimaryClick,
      onSecondaryClick,
      size = "medium",
      open: controlledOpen,
      defaultOpen = false,
      onOpenChange,
      containerClassName,
      menuClassName,
      showChevron = false,
      emptyIcon,
      disableFooter = false,
      showFooter,
      ...props
    },
    ref
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const isOpen =
      controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    const handleOpenChange = (newOpen: boolean) => {
      if (controlledOpen === undefined) {
        setUncontrolledOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    };

    const toggleOpen = () => {
      handleOpenChange(!isOpen);
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Close on escape key
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isOpen]);

    const sizeMap = {
      small: "w-64",
      medium: "w-80",
      large: "w-96",
    };

    return (
      <div
        ref={dropdownRef}
        className={cn("relative inline-block", containerClassName)}
        {...props}
      >
        {/* Trigger */}
        {trigger && (
          <div onClick={toggleOpen} className="cursor-pointer">
            {trigger}
          </div>
        )}

        {/* Dropdown Menu */}
        {isOpen && (
          <DropdownMenu
            ref={ref}
            items={items}
            customContent={customContent}
            sectionHeading={sectionHeading}
            isLoading={isLoading}
            isEmpty={isEmpty}
            emptyTitle={emptyTitle}
            emptyDescription={emptyDescription}
            emptyLinkText={emptyLinkText}
            onEmptyLinkClick={onEmptyLinkClick}
            primaryButtonText={primaryButtonText}
            secondaryButtonText={secondaryButtonText}
            onPrimaryClick={onPrimaryClick}
            onSecondaryClick={onSecondaryClick}
            showChevron={showChevron}
            emptyIcon={emptyIcon}
            disableFooter={disableFooter}
            showFooter={showFooter}
            onClose={() => handleOpenChange(false)}
            className={cn("absolute z-50 mt-2", menuClassName, className)}
            width={sizeMap[size]}
          />
        )}
      </div>
    );
  }
);

Dropdown.displayName = "Dropdown";

export { dropdownVariants };
